import StyledContainer from '@/components/styled/StyledContainer';
import StyledInput from '@/components/styled/StyledInput';
import StyledListHeader from '@/components/styled/StyledListHeader';
import StyledNextPreviousButtons from '@/components/styled/StyledNextPreviousButtons';
import StyledTable from '@/components/styled/StyledTable';
import { PAGE_SIZE } from '@/constants/pageSize';
import {
  useAcceptReservationRequest,
  useGetIncomingReservationRequests,
  useRefuseReservationRequest,
} from '@/hooks/useEstimate';
import useTableControls from '@/hooks/useTableControls';
import { LabelValue } from '@/types/global';
import {
  IncomingReservation,
  RESERVATION_COLUMNS,
} from '@/types/reservations/IncomingReservationColumnDef';
import { useCallback, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import SearchIcon from '@/assets/icons/search.svg?react';
import StyledSelect from '@/components/styled/StyledSelect';
import { useNavigate } from 'react-router';
import { PATHS } from '@/constants/Paths';
import { useToast } from '@/hooks/useToast';
import { Row } from '@tanstack/react-table';
import { EstimateStatus } from '@/types/estimates/EstimateStatus';
import { getEstimateStatusLabel } from '@/utils/estimates/getEstimateStatusLabel';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';

type ReservationRequestsTableFilters = {
  clientName: string;
  agency?: LabelValue<string>;
  country?: LabelValue<string>;
  agent?: LabelValue<string>;
  status?: LabelValue<string>;
};

const ReservationRequestsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const getTranslatedConst = useTranslatedConst();
  const { control } = useForm<ReservationRequestsTableFilters>({
    mode: 'onChange',
    defaultValues: {
      clientName: '',
      agency: undefined,
      country: undefined,
      agent: undefined,
      status: undefined,
    },
  });

  const clientNameValue = useWatch({ control, name: 'clientName' });
  const countryValue = useWatch({ control, name: 'country' });
  const agencyValue = useWatch({ control, name: 'agency' });
  const agentValue = useWatch({ control, name: 'agent' });
  const statusValue = useWatch({ control, name: 'status' });

  const filters = useMemo(
    () => ({
      clientName: clientNameValue,
      country: countryValue?.value ?? '',
      agency: agencyValue?.value ?? '',
      agent: agentValue?.value ?? '',
      status: statusValue?.value ?? '',
    }),
    [
      clientNameValue,
      countryValue?.value,
      agencyValue?.value,
      agentValue?.value,
      statusValue?.value,
    ],
  );

  const { page, setPage, sorting, setSorting, sortField, sortOrder } =
    useTableControls();

  const { data: incomingReservations, isSuccess } =
    useGetIncomingReservationRequests(
      {
        page,
        pageSize: PAGE_SIZE,
        sortField,
        sortOrder,
        filters,
      },
      (data) => ({
        ...data,
        agents: data.agents.map(({ id, firstname, lastname }) => ({
          label: `${firstname} ${lastname}`,
          value: id,
        })),
        countries: [
          ...new Set(data.countries.flatMap(({ countries }) => countries)),
        ].map((country) => ({ label: country, value: country })),
        agencies: data.agencies.map(({ id, name }) => ({
          label: name,
          value: id,
        })),
        statuses: data.statuses.map(({ status }) => ({
          label: getEstimateStatusLabel(status, true, t),
          value: status,
        })),
      }),
    );

  const { mutateAsync: acceptReservationRequest } =
    useAcceptReservationRequest();
  const { mutateAsync: refuseReservationRequest } =
    useRefuseReservationRequest();

  const { toastAsync } = useToast();

  const acceptReservation = useCallback(
    (row: Row<IncomingReservation>) =>
      toastAsync(acceptReservationRequest(row.original.id), {
        success: t('reservations.reservationRequestAccepted'),
      }),
    [acceptReservationRequest, t, toastAsync],
  );

  const refuseReservation = useCallback(
    (row: Row<IncomingReservation>) =>
      toastAsync(refuseReservationRequest(row.original.id), {
        success: t('reservations.reservationRequestRefused'),
      }),
    [refuseReservationRequest, t, toastAsync],
  );

  const navigateToReservationRequest = useCallback(
    (row: Row<IncomingReservation>) => {
      navigate(
        PATHS.home.reservations.requests.view.replace(':estimateId', row.id),
      );
    },
    [navigate],
  );

  return (
    <StyledContainer>
      <StyledListHeader
        title={t('reservations.reservationRequests')}
        subTitle={t('reservations.subtitle')}
      />
      <div className="flex flex-col w-full h-auto p-6 gap-6">
        <div className="flex flex-col gap-2">
          <span className="text-xs font-normal text-gray-600">
            {t('reservations.filterBy')}
          </span>
          <div className="flex flex-col md:flex-row justify-between gap-4">
            <div className="flex flex-col md:flex-row gap-4">
              <StyledSelect
                className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                control={control}
                options={incomingReservations?.agencies ?? []}
                name="agency"
                placeholder={t('reservations.agency')}
                isClearable={true}
              />
              <StyledSelect
                className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                control={control}
                options={incomingReservations?.countries ?? []}
                name="country"
                placeholder={t('reservations.country')}
                isClearable={true}
              />
              <StyledSelect
                className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                control={control}
                options={incomingReservations?.agents ?? []}
                name="agent"
                placeholder={t('reservations.agent')}
                isClearable={true}
              />
              <StyledSelect
                className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                control={control}
                options={incomingReservations?.statuses ?? []}
                name="status"
                placeholder={t('reservations.status')}
                isClearable={true}
              />
            </div>
            <div className="w-full md:w-1/3 lg:w-1/4 2xl:w-1/5">
              <StyledInput
                control={control}
                name="clientName"
                placeholder={t('reservations.quickSearch')}
                icon={<SearchIcon className="w-5 h-5 stroke-gray-500" />}
              />
            </div>
          </div>
        </div>
        <StyledTable
          columns={getTranslatedConst(RESERVATION_COLUMNS)}
          data={incomingReservations?.reservationRequests ?? []}
          getRowId={(row) => row.id}
          sorting={sorting}
          setSorting={setSorting}
          onViewRow={navigateToReservationRequest}
          onRowClick={navigateToReservationRequest}
          acceptRefuseEnablePredicate={(row) =>
            row.original.status === EstimateStatus.ESTIMATE
          }
          onAcceptRow={acceptReservation}
          onRefuseRow={refuseReservation}
        />
        {isSuccess && (
          <StyledNextPreviousButtons
            page={page}
            pageSize={PAGE_SIZE}
            setPage={setPage}
            dataLength={incomingReservations.total}
            label={t('reservations.request')}
          />
        )}
      </div>
    </StyledContainer>
  );
};

export default ReservationRequestsPage;
